import Languages from '../../../../utils/Languages.js'
import {escapedTemplate} from '../../../utils/escapeHtml.js'
import {TrackTraceMailContent} from '../../../../utils/trackTraceMail.js'
import {button, card, codeBox, divider, field, footer, iconUrl, imageUrl, intro, mailColors, mailDocument, multiline, notice, stack, text, FONT} from './mailLayout.js'

// Mail to the recipient of an order in the style of the Track & Trace page (#3845), for resellers with the new page.
// The content comes from trackTraceMailContent (utils/trackTraceMail.ts); the other order mails use
// emailNotificationTemplate.

const lines = (value: string) => value.replace(/\n/g, '<br/>')

function progressBar(done: number | null, color: string): string {
    if (!done) {
        return ''
    }

    const segments = [1, 2, 3, 4].map((segment) => `<td width="25%" style="padding: 0 ${segment === 4 ? 0 : 3}px 0 0;"><div style="height: 4px; line-height: 4px; font-size: 4px; border-radius: 2px; background-color: ${segment <= done ? color : mailColors.line};">&nbsp;</div></td>`)

    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; border-collapse: collapse;"><tr>${segments.join('')}</tr></table>`
}

function statusBlock(content: TrackTraceMailContent, color: string): string {
    const moment = content.moment

    return stack([
        progressBar(content.progress, color),
        text(content.status, {size: 14, lineHeight: 20, bold: true, color: content.warn ? mailColors.warnInk : mailColors.ink}),
        moment && [
            text(moment.label, {size: 12, lineHeight: 16, color: mailColors.text}),
            `<div class="vl-headline" style="font-family: ${FONT}; font-size: 28px; line-height: 36px; color: ${mailColors.ink};">${moment.day}</div>`,
            moment.time ? text(moment.time, moment.timeMuted ? {color: mailColors.text} : {size: 18, lineHeight: 26}) : ''
        ].join('')
    ], 8)
}

function actionsBlock(actions: TrackTraceMailContent['actions'], baseUrl?: string): string {
    if (!actions.length) {
        return ''
    }

    const width = Math.floor(100 / actions.length)

    const tiles = actions.map(({icon, label, href}, index) => `
        <td class="vl-tile" width="${width}%" valign="top" style="width: ${width}%; padding: 0 ${index === actions.length - 1 ? 0 : 8}px 0 0;">
            <a class="vl-tile-link" href="${href}" target="_blank" style="display: block; text-decoration: none; border: 1px solid ${mailColors.line}; border-radius: 8px; padding: 12px 4px; text-align: center; background-color: ${mailColors.surface};">
                <img class="vl-tile-icon" src="${iconUrl(icon, baseUrl)}" width="24" height="24" alt="" style="display: inline-block; width: 24px; height: 24px; border: 0; vertical-align: middle;">
                <span class="vl-tile-label" style="display: block; font-family: ${FONT}; font-size: 13px; line-height: 18px; font-weight: 500; color: ${mailColors.ink}; padding-top: 4px;">${label}</span>
            </a>
        </td>`)

    return card([
        stack([
            text(Languages.mailActionsTitle.NL, {size: 16, lineHeight: 24, bold: true}),
            text(Languages.mailActionsIntro.NL, {color: mailColors.text})
        ], 4),
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; border-collapse: collapse;"><tr>${tiles.join('')}</tr></table>`
    ])
}

function messageBlock(message: string, logo: string, team: string, baseUrl?: string): string {
    if (!message) {
        return ''
    }

    const logoSrc = imageUrl(logo, baseUrl)

    return card([
        logoSrc && `<img src="${logoSrc}" alt="Logo" style="display: block; max-height: 32px; max-width: 140px; width: auto; height: auto; border: 0;">`,
        text(message, {size: 16, lineHeight: 24}),
        team && text(Languages.mailTeamOf.NL(team), {size: 12, lineHeight: 16, color: mailColors.text})
    ], {gap: 12})
}

const template = ({logo, color = '#1F2D3D', photo = '', message, content, recipientMessage = '', resellerName = ''}: {
    logo: string
    color?: string
    // The mobile photo of the Track & Trace page, when the reseller shows it
    photo?: string
    // The reseller's message for this notification, its placeholder values are escaped in mail.ts
    message: string
    content: TrackTraceMailContent
    // The message to recipients of the Track & Trace page, when the reseller shows it
    recipientMessage?: string
    resellerName?: string
}, baseUrl?: string) => {
    return mailDocument({
        logo,
        color,
        photo,
        baseUrl,
        content: stack([
            intro(multiline(message)),
            card([
                statusBlock(content, color),
                divider(),
                stack(content.rows.map((row) => field(row.label, lines(row.value), iconUrl(row.icon, baseUrl))), 12),
                content.notices.length && notice(content.notices, {title: content.notices.length > 1 ? Languages.mailGoodToKnow.NL : '', warn: content.noticesWarn, baseUrl}),
                content.code && codeBox(content.code.value, content.code.label),
                button(content.button.label, content.button.href, color)
            ]),
            actionsBlock(content.actions, baseUrl),
            messageBlock(recipientMessage, logo, resellerName, baseUrl)
        ]),
        footer: footer(content.footer)
    })
}

export default escapedTemplate(template)
